import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Filter, LayoutGrid, List, ArrowUpDown,
  ChevronLeft, ChevronRight, RefreshCw, Star,
  SlidersHorizontal, X, Loader2, FileText, EyeOff, Eye, Sparkles, Clock
} from 'lucide-react';
import { Contract, SetAsideType } from '../../types';
import { useContracts } from '../../hooks/useContracts';
import { useSubscription } from '../../hooks/useSubscription';
import { useDebounce } from '../../hooks/useDebounce';
import { useScoreCache } from '../../hooks/useScoreCache';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { Footer } from '../Footer';
import { FilterDrawer } from './FilterDrawer';
import { ModernContractCard } from './ModernContractCard';
import { AiBestMatches } from '../AiBestMatches';
import { PipelineStrip, PipelineStat } from '../ui/PipelineStrip';
import { CommandPalette } from '../ui/CommandPalette';

// Import proposal-related components
import { ContractSlideout } from '../ContractSlideout';
import { getPipelineSummary, STAGE_META, type PipelineSummary } from '../../lib/pipeline-service';
import { ProposalGenerator } from '../ProposalGenerator';
import { ProposalTypeChoice } from '../ProposalTypeChoice';
import { SolicitationDocumentsStep } from '../SolicitationDocumentsStep';
import { EnhancedProposalGenerator } from '../EnhancedProposalGenerator';
import { OrchestratedProposalGenerator } from '../OrchestratedProposalGenerator';
import { ProposalDraftModal } from '../ProposalDraftModal';
import { useNavigate } from 'react-router-dom';
import { AdminDataManager } from '../AdminDataManager';
import { GeneratedProposal, CollectedData, ContractData } from '../../services/orchestrated-proposal-service';
import { SourceGroundedProposalOutput } from '../../types/source-grounded';

interface FilterState {
  keywords?: string;
  naicsCode?: string[];
  status?: ('open' | 'pending' | 'closed')[];
  agency?: string[];
  minAmount?: number;
  maxAmount?: number;
  setAsides?: SetAsideType[];
  state?: string;
  page?: number;
  limit?: number;
  sortBy?: 'bidwin_score' | 'due_date' | 'posted_date' | 'funding_amount' | 'agency';
  sortOrder?: 'asc' | 'desc';
  excludePastDue?: boolean;
  pipelineStage?: 'watching' | 'pursuing' | 'submitted' | 'won' | 'lost' | 'skipped';
  dueWithinDays?: number;
  favoritesOnly?: boolean;
}

type ViewMode = 'grid' | 'list' | 'compact' | 'drafts' | 'ai';

const SORT_OPTIONS = [
  { value: 'bidwin_score|desc', label: 'Best Match (Score)' },
  { value: 'due_date|asc', label: 'Due Date (Soonest)' },
  { value: 'due_date|desc', label: 'Due Date (Latest)' },
  { value: 'posted_date|desc', label: 'Posted (Newest)' },
  { value: 'posted_date|asc', label: 'Posted (Oldest)' },
  { value: 'funding_amount|desc', label: 'Value (Highest)' },
  { value: 'funding_amount|asc', label: 'Value (Lowest)' },
  { value: 'agency|asc', label: 'Agency (A–Z)' },
  { value: 'agency|desc', label: 'Agency (Z–A)' },
];

export function ModernDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [proposalMap, setProposalMap] = useState<Record<string, boolean>>({});
  // notice_id/contract_id -> true for contracts with an in-progress generation session
  const [resumableMap, setResumableMap] = useState<Record<string, boolean>>({});
  const [pipeline, setPipeline] = useState<PipelineSummary | null>(null);
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Score caching
  const { isCalculating, progress } = useScoreCache();

  // Filter state - default to BidWin score (highest) and exclude past due
  const [filters, setFilters] = useState<FilterState>({
    keywords: '',
    naicsCode: [],
    status: ['open', 'pending'],
    agency: [],
    minAmount: 0,
    maxAmount: 10000000,
    setAsides: [],
    page: 0,
    limit: 12,
    sortBy: 'bidwin_score',
    sortOrder: 'desc',
    excludePastDue: true
  });

  const [appliedFilters, setAppliedFilters] = useState<FilterState>(filters);

  // Contract data
  const { contracts, loading, error, total, refresh } = useContracts(appliedFilters);
  const { canViewContract, incrementViews } = useSubscription();

  // Pagination
  const totalPages = Math.ceil(total / (filters.limit || 12));
  const currentPage = (filters.page || 0) + 1;

  // Contract interaction states
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [showProposalGenerator, setShowProposalGenerator] = useState(false);
  const [showProposalChoice, setShowProposalChoice] = useState(false);
  const [showDocumentsStep, setShowDocumentsStep] = useState(false);
  const [selectedProposalType, setSelectedProposalType] = useState<'basic' | 'enhanced' | 'source-grounded' | null>(null);
  const [showEnhancedProposal, setShowEnhancedProposal] = useState(false);
  const [showSourceGroundedProposal, setShowSourceGroundedProposal] = useState(false);
  const [showProposalDraft, setShowProposalDraft] = useState(false);
  const [proposalDraft, setProposalDraft] = useState<any>(null);
  const [sourceGroundedOutput, setSourceGroundedOutput] = useState<SourceGroundedProposalOutput | null>(null);

  // Load proposal status
  const loadProposalStatus = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('proposal_templates')
        .select('id, notice_id, contract_id, template_name')
        .eq('profile_id', user.id);

      if (error) throw error;

      const map = (data || []).reduce((acc, item) => {
        if (item.notice_id && item.notice_id !== 'null') acc[item.notice_id] = true;
        if (item.contract_id && item.contract_id !== 'null') acc[item.contract_id] = true;
        return acc;
      }, {} as Record<string, boolean>);

      setProposalMap(map);

      // Active checkpoint-generation sessions → "Resume" affordance. (No-op when
      // the checkpoint flow is off, since no sessions are created in that case.)
      const { data: sessions } = await supabase
        .from('proposal_generation_sessions')
        .select('notice_id, status')
        .eq('profile_id', user.id)
        .eq('status', 'active');
      const rmap = (sessions || []).reduce((acc, s: any) => {
        if (s.notice_id && s.notice_id !== 'null') acc[s.notice_id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setResumableMap(rmap);
    } catch (error) {
      console.error('Error loading proposal status:', error);
    }
  }, []);

  const loadPipeline = useCallback(async () => {
    if (!profile?.id) return;
    try {
      setPipeline(await getPipelineSummary(profile.id));
    } catch (e) {
      console.error('Error loading pipeline summary:', e);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile) {
      loadProposalStatus();
      loadPipeline();
    }
  }, [profile, loadProposalStatus, loadPipeline]);

  // Apply search when debounced value changes
  useEffect(() => {
    setAppliedFilters(prev => ({
      ...prev,
      keywords: debouncedSearch,
      page: 0
    }));
    setFilters(prev => ({
      ...prev,
      keywords: debouncedSearch,
      page: 0
    }));
  }, [debouncedSearch]);

  // Handlers
  const handleContractSelect = async (contract: Contract) => {
    try {
      const canView = await canViewContract();
      if (!canView) {
        toast.error('You have reached your monthly view limit. Please upgrade your plan.');
        return;
      }
      await incrementViews();
      setSelectedContract(contract);
    } catch (error) {
      console.error('Error handling contract selection:', error);
      toast.error('Failed to view contract details');
    }
  };

  const handleGenerateProposal = (contract: Contract) => {
    setSelectedContract(contract);
    setShowProposalChoice(true);
  };

  const handleProposalTypeChoice = (type: 'basic' | 'enhanced' | 'source-grounded') => {
    setShowProposalChoice(false);
    setSelectedProposalType(type);
    if (type === 'enhanced' || type === 'source-grounded') {
      setShowDocumentsStep(true);
    } else {
      setShowProposalGenerator(true);
    }
  };

  const handleDocumentsStepContinue = () => {
    setShowDocumentsStep(false);
    if (selectedProposalType === 'enhanced') {
      setShowEnhancedProposal(true);
    } else if (selectedProposalType === 'source-grounded') {
      setShowSourceGroundedProposal(true);
    }
  };

  const handleViewProposal = async (contract: Contract) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to view proposals');
        return;
      }

      const { data, error } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', user.id)
        .eq('notice_id', contract.notice_id)
        .single();

      if (error || !data) {
        toast.error('Could not find saved proposal');
        return;
      }

      // Single canonical viewer: open the full-page Workspace (with inline
      // editing) instead of the legacy modal.
      navigate(`/workspace/${data.id}`);
      return;

      // eslint-disable-next-line no-unreachable
      const contentJson = data.content_json;
      if (!contentJson) {
        toast.error('Proposal data is empty');
        return;
      }

      const isSourceGrounded = contentJson.proposalType === 'source-grounded' || contentJson.sourceGroundedOutput;

      if (isSourceGrounded && contentJson.sourceGroundedOutput) {
        const output = contentJson.sourceGroundedOutput as SourceGroundedProposalOutput;
        const draft = {
          coverLetter: output.proposal.coverLetter?.content || contentJson.coverLetter || '',
          technicalSections: [
            { title: 'Executive Summary', content: output.proposal.executiveSummary?.content || '' },
            { title: 'Technical Approach', content: output.proposal.technicalApproach?.content || '' },
            { title: 'Management Approach', content: output.proposal.managementApproach?.content || '' },
            { title: 'Past Performance', content: output.proposal.pastPerformance?.content || '' },
            { title: 'Personnel', content: output.proposal.personnel?.content || '' },
            { title: 'Pricing Strategy', content: output.proposal.pricingStrategy?.content || '' },
            { title: 'Compliance', content: output.proposal.compliance?.content || '' },
          ].filter(s => s.content),
          feedback: contentJson.feedback || '',
          contractAnalysis: '',
          profileAnalysis: '',
          deliverablesData: contentJson.deliverablesData || null,
          message: `Source-grounded proposal loaded`,
        };

        setProposalDraft(draft);
        setSourceGroundedOutput(output);
        setShowProposalDraft(true);
        toast.success('Proposal loaded successfully');
      } else {
        const draft: any = {
          coverLetter: contentJson.coverLetter || '',
          technicalSections: contentJson.technicalSections || [],
          feedback: typeof contentJson.feedback === 'string' ? contentJson.feedback : '',
          contractAnalysis: contentJson.contractAnalysis || '',
          profileAnalysis: contentJson.profileAnalysis || '',
          deliverablesData: contentJson.deliverablesData || null,
          message: 'Saved proposal loaded',
        };

        if (contentJson.aiFeedback) draft.aiFeedback = contentJson.aiFeedback;
        if (contentJson.gapAnalysis) draft.gapAnalysis = contentJson.gapAnalysis;
        if (contentJson.winProbability) draft.winProbability = contentJson.winProbability;
        if (contentJson.proposalType === 'orchestrated') draft.proposalType = 'orchestrated';

        setProposalDraft(draft);
        setSourceGroundedOutput(null);
        setShowProposalDraft(true);
        toast.success('Proposal loaded successfully');
      }
    } catch (error) {
      console.error('Error viewing proposal:', error);
      toast.error('Failed to load proposal');
    }
  };

  const handlePageChange = (newPage: number) => {
    const updatedFilters = { ...filters, page: newPage - 1 };
    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters, page: 0 });
    setFilters(prev => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    const resetFilters: FilterState = {
      keywords: '',
      naicsCode: [],
      status: ['open', 'pending'],
      agency: [],
      minAmount: 0,
      maxAmount: 10000000,
      setAsides: [],
      page: 0,
      limit: 12,
      sortBy: 'bidwin_score',
      sortOrder: 'desc',
      excludePastDue: true
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setSearchTerm('');
    if (viewMode === 'drafts') {
      setViewMode('grid');
    }
  };

  const handleSortChange = (value: string) => {
    const [sortBy, sortOrder] = value.split('|') as [FilterState['sortBy'], FilterState['sortOrder']];
    const newFilters = { ...filters, sortBy, sortOrder, page: 0 };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return (
      (appliedFilters.setAsides?.length || 0) +
      (appliedFilters.naicsCode?.length || 0) +
      (appliedFilters.state ? 1 : 0) +
      (appliedFilters.minAmount && appliedFilters.minAmount > 0 ? 1 : 0) +
      (appliedFilters.maxAmount && appliedFilters.maxAmount < 10000000 ? 1 : 0) +
      (appliedFilters.status?.length !== 2 ? 1 : 0)
    );
  }, [appliedFilters]);

  // Filter contracts for drafts view
  const displayedContracts = useMemo(() => {
    if (viewMode === 'drafts') {
      return contracts.filter(c => proposalMap[c.id] || proposalMap[c.notice_id]);
    }
    return contracts;
  }, [contracts, viewMode, proposalMap]);

  // Pipeline strip — one quiet summary row replacing the six stat cards.
  const pipelineStats: PipelineStat[] = useMemo(() => {
    const now = Date.now();
    const closingSoon = contracts.filter(c => {
      if (!c.dueDate) return false;
      const days = Math.ceil((new Date(c.dueDate).getTime() - now) / 86400000);
      return days >= 0 && days <= 5;
    }).length;
    const saved = contracts.filter(c => c.isFavorited).length;
    const drafts = Object.keys(proposalMap).length;
    return [
      { value: total.toLocaleString(), label: 'open' },
      { value: closingSoon, label: 'closing soon', urgent: closingSoon > 0 },
      { value: saved, label: 'saved' },
      { value: drafts, label: 'drafts' },
    ];
  }, [contracts, total, proposalMap]);

  // "Best fits for you" — lead with the top 3 best-fit, soonest-closing.
  const bestFits = useMemo(() => {
    const dueRank = (c: Contract) => {
      if (!c.dueDate) return Number.MAX_SAFE_INTEGER;
      const days = Math.ceil((new Date(c.dueDate).getTime() - Date.now()) / 86400000);
      return days < 0 ? Number.MAX_SAFE_INTEGER - 1 : days;
    };
    return [...contracts]
      .sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0) || dueRank(a) - dueRank(b))
      .slice(0, 3);
  }, [contracts]);

  // Show the best-fits lead only on the first page of the default browse view,
  // when the user hasn't narrowed with search/filters.
  const showBestFits =
    viewMode === 'grid' &&
    (filters.page || 0) === 0 &&
    !debouncedSearch &&
    activeFilterCount === 0 &&
    bestFits.length > 0;

  // Determine card variant based on view mode
  const getCardVariant = () => {
    if (viewMode === 'list') return 'row';
    if (viewMode === 'compact') return 'compact';
    return 'card';
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-44 bg-bw-surface-subtle border border-bw-border rounded-xl" />
    </div>
  );

  const openCount = total.toLocaleString();
  const closingSoonCount = pipelineStats[1]?.value ?? 0;

  return (
    <div className="min-h-screen bg-bw-app">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Score Calculation Banner */}
        {isCalculating && (
          <div className="mb-4 bg-bw-primary-tint border border-servet-blue/20 rounded-xl p-4 flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-servet-dark-blue">Calculating BidWin scores...</p>
              <div className="mt-1 w-full bg-bw-primary/20 rounded-full h-1.5">
                <div
                  className="bg-bw-primary h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <span className="text-sm text-bw-primary font-medium flex-shrink-0">{progress}%</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-bw-ink tracking-tight">Contract Opportunities</h1>
            <p className="mt-0.5 text-sm text-bw-muted">
              <span className="font-mono">{openCount}</span> open · <span className="font-mono">{closingSoonCount}</span> closing soon
            </p>
          </div>
          <div className="flex items-center gap-2 self-start">
            {/* ⌘K search */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-bw-surface border border-bw-border rounded-lg text-sm font-medium text-bw-muted hover:bg-bw-surface-subtle transition-colors"
              title="Search (⌘K)"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Search</span>
              <kbd className="hidden sm:inline px-1.5 py-0.5 bg-bw-surface-subtle border border-bw-border rounded text-[10px] font-mono text-bw-muted">⌘K</kbd>
            </button>
            {/* View switch: Browse all vs AI Best Matches */}
            <div className="flex items-center bg-bw-surface rounded-lg border border-bw-border p-1 shadow-card">
              <button
                onClick={() => setViewMode('grid')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  viewMode !== 'ai' ? 'bg-bw-primary text-white' : 'text-bw-muted hover:text-bw-ink'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Browse All</span>
              </button>
              <button
                onClick={() => setViewMode('ai')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  viewMode === 'ai' ? 'bg-bw-primary text-white' : 'text-bw-primary hover:bg-bw-primary-tint'
                }`}
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">AI Best Matches</span>
              </button>
            </div>
          </div>
        </div>

        {/* AI Best Matches view */}
        {viewMode === 'ai' && (
          <AiBestMatches
            onSelectContract={handleContractSelect}
            onGenerateProposal={handleGenerateProposal}
          />
        )}

        {/* Pipeline strip — replaces the six stat cards */}
        {viewMode !== 'ai' && (
          <div className="mb-5">
            <PipelineStrip
              stats={pipelineStats}
              action={
                <button
                  onClick={() => setViewMode('grid')}
                  className="text-[11px] font-semibold text-bw-primary hover:underline whitespace-nowrap"
                >
                  View all →
                </button>
              }
            />
          </div>
        )}

        {/* Capture pipeline funnel — counts by stage + win rate + pipeline value */}
        {viewMode !== 'ai' && pipeline && pipeline.total > 0 && (
          <div className="mb-5 flex items-center gap-2 flex-wrap rounded-xl border border-bw-border bg-bw-surface px-3.5 py-2.5">
            <span className="text-[11px] font-bold text-bw-muted uppercase tracking-wide mr-1">Pipeline</span>
            {(['pursuing', 'submitted', 'won', 'lost'] as const).map((s) => {
              const active = appliedFilters.pipelineStage === s;
              const toggle = () => {
                const next = active ? undefined : s;
                setFilters(prev => ({ ...prev, pipelineStage: next, page: 0 }));
                setAppliedFilters(prev => ({ ...prev, pipelineStage: next, page: 0 }));
              };
              return (
                <button
                  key={s}
                  onClick={toggle}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${STAGE_META[s].pill} ${active ? 'ring-2 ring-bw-primary/40' : 'opacity-90 hover:opacity-100'}`}
                  title={active ? 'Clear filter' : `Show only ${STAGE_META[s].label}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[s].dot}`} />
                  {pipeline.counts[s]} {STAGE_META[s].label}
                </button>
              );
            })}
            {pipeline.winRate !== null && (
              <span className="text-[11px] text-bw-ink ml-auto">
                Win rate <strong>{Math.round(pipeline.winRate * 100)}%</strong>
              </span>
            )}
            {pipeline.pipelineValue > 0 && (
              <span className="text-[11px] text-bw-ink">
                Won value <strong>${Math.round(pipeline.pipelineValue).toLocaleString()}</strong>
              </span>
            )}
          </div>
        )}

        {/* Best fits for you — lead with the top 3 best-fit, soonest-closing */}
        {showBestFits && !loading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-[13px] font-bold text-bw-ink">Best fits for you</h2>
              <button
                onClick={() => setViewMode('ai')}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-bw-primary hover:underline"
              >
                <Sparkles className="h-3.5 w-3.5" />
                AI scan
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bestFits.map(contract => (
                <ModernContractCard
                  key={`best-${contract.id}`}
                  contract={contract}
                  onSelect={handleContractSelect}
                  selected={selectedContract?.id === contract.id}
                  hasActiveSession={resumableMap[contract.id] || resumableMap[contract.notice_id]}
                  hasProposal={proposalMap[contract.id] || proposalMap[contract.notice_id]}
                  variant="card"
                />
              ))}
            </div>
          </div>
        )}

        {/* All opportunities heading */}
        {viewMode === 'grid' && !loading && displayedContracts.length > 0 && (
          <h2 className="text-[13px] font-bold text-bw-ink mb-2.5">All opportunities</h2>
        )}

        {/* Search and Controls Bar */}
        {viewMode !== 'ai' && (
        <div className="bg-bw-surface rounded-xl shadow-card border border-bw-border p-4 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-bw-muted" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search contracts..."
                  className="w-full pl-10 pr-4 py-3 border border-bw-border rounded-lg bg-bw-surface text-bw-ink focus:ring-2 focus:ring-bw-primary focus:border-transparent transition-shadow"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-bw-surface-subtle rounded-full"
                  >
                    <X className="h-4 w-4 text-bw-muted" />
                  </button>
                )}
              </div>

              {/* Filter Button */}
              <button
                onClick={() => setShowFilters(true)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-3 border rounded-xl font-medium transition-colors ${
                  activeFilterCount > 0
                    ? 'border-servet-blue bg-bw-primary-tint text-bw-primary hover:bg-bw-primary/10'
                    : 'border-bw-border text-bw-ink hover:bg-bw-surface-subtle'
                }`}
              >
                <SlidersHorizontal className="h-5 w-5" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 bg-bw-primary text-white text-xs font-medium rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Controls Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {/* Results count and quick toggles */}
              <div className="flex items-center gap-3 order-2 sm:order-1 flex-wrap">
                <p className="text-sm text-bw-muted">
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </span>
                  ) : viewMode === 'drafts' ? (
                    <>
                      Showing <span className="font-medium">{displayedContracts.length}</span> draft{displayedContracts.length !== 1 ? 's' : ''}
                    </>
                  ) : (
                    <>
                      Showing <span className="font-medium">{displayedContracts.length}</span> of{' '}
                      <span className="font-medium">{total.toLocaleString()}</span> contracts
                    </>
                  )}
                </p>

                {/* Hide Past Due Toggle */}
                <button
                  onClick={() => {
                    const newFilters = { ...filters, excludePastDue: !filters.excludePastDue, page: 0 };
                    setFilters(newFilters);
                    setAppliedFilters(newFilters);
                  }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filters.excludePastDue
                      ? 'bg-bw-primary-tint text-bw-primary border border-servet-blue/20'
                      : 'bg-bw-surface-subtle text-bw-muted hover:bg-gray-200'
                  }`}
                  title={filters.excludePastDue ? 'Show past due contracts' : 'Hide past due contracts'}
                >
                  {filters.excludePastDue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{filters.excludePastDue ? 'Hiding Past Due' : 'Show All'}</span>
                </button>

                {/* Closing-soon quick filter — cycles off -> 7 -> 14 -> 30 days */}
                <button
                  onClick={() => {
                    const opts = [undefined, 7, 14, 30] as (number | undefined)[];
                    const next = opts[(opts.indexOf(filters.dueWithinDays) + 1) % opts.length];
                    const nf = { ...filters, dueWithinDays: next, page: 0 };
                    setFilters(nf);
                    setAppliedFilters(nf);
                  }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filters.dueWithinDays
                      ? 'bg-fit-moderate-bg text-fit-moderate border border-amber-300'
                      : 'bg-bw-surface-subtle text-bw-muted hover:bg-gray-200'
                  }`}
                  title="Filter by deadline window"
                >
                  <Clock className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{filters.dueWithinDays ? `Due ≤${filters.dueWithinDays}d` : 'Closing soon'}</span>
                </button>

                {/* My Drafts Toggle */}
                <button
                  onClick={() => setViewMode(viewMode === 'drafts' ? 'grid' : 'drafts')}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    viewMode === 'drafts'
                      ? 'bg-success-bg text-success border border-green-200'
                      : 'bg-bw-surface-subtle text-bw-muted hover:bg-gray-200'
                  }`}
                  title="View contracts with proposal drafts"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">My Drafts</span>
                  {Object.keys(proposalMap).length > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      viewMode === 'drafts' ? 'bg-green-200 text-success' : 'bg-gray-200 text-bw-ink'
                    }`}>
                      {Object.keys(proposalMap).length}
                    </span>
                  )}
                </button>
              </div>

              {/* View and Sort Controls */}
              <div className="flex items-center gap-3 order-1 sm:order-2">
                {/* Sort Dropdown */}
                <div className="relative flex-1 sm:flex-none">
                  <select
                    value={`${appliedFilters.sortBy || 'bidwin_score'}|${appliedFilters.sortOrder || 'desc'}`}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="appearance-none w-full sm:w-auto pl-3 pr-10 py-2 text-sm border border-bw-border rounded-lg bg-bw-surface hover:bg-bw-surface-subtle focus:ring-2 focus:ring-bw-primary focus:border-transparent cursor-pointer"
                  >
                    {SORT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ArrowUpDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted pointer-events-none" />
                </div>

                {/* View Mode Toggle */}
                <div className="hidden sm:flex items-center bg-bw-surface-subtle rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-bw-surface shadow-sm text-bw-primary' : 'text-bw-muted hover:text-bw-ink'
                    }`}
                    title="Grid view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-bw-surface shadow-sm text-bw-primary' : 'text-bw-muted hover:text-bw-ink'
                    }`}
                    title="List view"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => refresh()}
                  disabled={loading}
                  className="p-2 border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`h-4 w-4 text-bw-muted ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Error State */}
        {viewMode !== 'ai' && error && (
          <div className="bg-urgent-bg border border-urgent-border rounded-xl p-6 text-center mb-6">
            <p className="text-urgent mb-4">Failed to load contracts</p>
            <button
              onClick={() => refresh()}
              className="px-4 py-2 bg-urgent text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Contracts Grid/List */}
        {viewMode !== 'ai' && !error && (
          <>
            {loading ? (
              <div className={`grid gap-4 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                  : 'grid-cols-1'
              }`}>
                {[...Array(8)].map((_, i) => (
                  <LoadingSkeleton key={i} />
                ))}
              </div>
            ) : displayedContracts.length === 0 ? (
              <div className="bg-bw-surface rounded-xl border border-bw-border p-12 text-center">
                <div className="w-16 h-16 bg-bw-surface-subtle rounded-full flex items-center justify-center mx-auto mb-4">
                  {viewMode === 'drafts' ? (
                    <FileText className="h-8 w-8 text-bw-muted" />
                  ) : (
                    <Search className="h-8 w-8 text-bw-muted" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-bw-ink mb-2">
                  {viewMode === 'drafts' ? 'No proposal drafts' : 'No contracts found'}
                </h3>
                <p className="text-bw-muted mb-6">
                  {viewMode === 'drafts'
                    ? 'You haven\'t created any proposal drafts yet. Select a contract and generate a proposal to get started.'
                    : 'Try adjusting your filters or search terms'}
                </p>
                <button
                  onClick={handleResetFilters}
                  className="px-6 py-2 bg-bw-primary text-white rounded-lg hover:bg-servet-dark-blue transition-colors"
                >
                  {viewMode === 'drafts' ? 'Browse Contracts' : 'Reset Filters'}
                </button>
              </div>
            ) : (
              <>
                {/* Grid/Card View */}
                {(viewMode === 'grid' || viewMode === 'drafts') && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {displayedContracts.map(contract => (
                      <ModernContractCard
                        key={contract.id}
                        contract={contract}
                        onSelect={handleContractSelect}
                        selected={selectedContract?.id === contract.id}
                        hasActiveSession={resumableMap[contract.id] || resumableMap[contract.notice_id]}
                        hasProposal={proposalMap[contract.id] || proposalMap[contract.notice_id]}
                        variant="card"
                      />
                    ))}
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
                    {displayedContracts.map(contract => (
                      <ModernContractCard
                        key={contract.id}
                        contract={contract}
                        onSelect={handleContractSelect}
                        selected={selectedContract?.id === contract.id}
                        hasActiveSession={resumableMap[contract.id] || resumableMap[contract.notice_id]}
                        hasProposal={proposalMap[contract.id] || proposalMap[contract.notice_id]}
                        variant="row"
                      />
                    ))}
                  </div>
                )}

                {/* Compact View (Mobile) */}
                {viewMode === 'compact' && (
                  <div className="space-y-3">
                    {displayedContracts.map(contract => (
                      <ModernContractCard
                        key={contract.id}
                        contract={contract}
                        onSelect={handleContractSelect}
                        selected={selectedContract?.id === contract.id}
                        hasActiveSession={resumableMap[contract.id] || resumableMap[contract.notice_id]}
                        hasProposal={proposalMap[contract.id] || proposalMap[contract.notice_id]}
                        variant="compact"
                      />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 border border-bw-border rounded-lg hover:bg-bw-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    <div className="flex items-center gap-1">
                      {/* First page */}
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-2 text-sm border border-bw-border rounded-lg hover:bg-bw-surface-subtle"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-2 text-bw-muted">...</span>}
                        </>
                      )}

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                        if (pageNum > totalPages || pageNum < 1) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              pageNum === currentPage
                                ? 'bg-bw-primary text-white'
                                : 'border border-bw-border hover:bg-bw-surface-subtle'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      {/* Last page */}
                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-2 text-bw-muted">...</span>}
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-2 text-sm border border-bw-border rounded-lg hover:bg-bw-surface-subtle"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-bw-border rounded-lg hover:bg-bw-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Footer />

      {/* ⌘K Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpen={() => setCommandPaletteOpen(true)}
        onClose={() => setCommandPaletteOpen(false)}
        contracts={contracts}
        onSelectContract={handleContractSelect}
        onGenerateProposal={selectedContract ? () => handleGenerateProposal(selectedContract) : undefined}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />

      {/* Contract Slideout */}
      {selectedContract && !showProposalGenerator && !showProposalChoice && !showEnhancedProposal && !showSourceGroundedProposal && (
        <ContractSlideout
          contract={selectedContract}
          onClose={() => { setSelectedContract(null); loadPipeline(); refresh(); }}
          onGenerateProposal={() => handleGenerateProposal(selectedContract)}
          onViewProposal={() => handleViewProposal(selectedContract)}
          hasProposal={proposalMap[selectedContract.id] || proposalMap[selectedContract.notice_id] || false}
        />
      )}

      {/* Proposal Type Choice */}
      {showProposalChoice && selectedContract && (
        <ProposalTypeChoice
          contract={selectedContract}
          onClose={() => {
            setShowProposalChoice(false);
            setSelectedContract(null);
          }}
          onSelectType={handleProposalTypeChoice}
        />
      )}

      {/* Documents Step */}
      {showDocumentsStep && selectedContract && selectedProposalType && (
        <SolicitationDocumentsStep
          contract={selectedContract}
          proposalType={selectedProposalType}
          onBack={() => {
            setShowDocumentsStep(false);
            setShowProposalChoice(true);
          }}
          onContinue={handleDocumentsStepContinue}
          onSkip={handleDocumentsStepContinue}
        />
      )}

      {/* Enhanced Proposal Generator */}
      {showEnhancedProposal && selectedContract && (
        <EnhancedProposalGenerator
          contract={selectedContract}
          onClose={() => {
            setShowEnhancedProposal(false);
            setSelectedContract(null);
          }}
          onSuccess={() => {
            setShowEnhancedProposal(false);
            loadProposalStatus();
            handleViewProposal(selectedContract);
          }}
        />
      )}

      {/* Source-Grounded Proposal Generator */}
      {showSourceGroundedProposal && selectedContract && (
        <OrchestratedProposalGenerator
          contract={selectedContract}
          onClose={() => {
            setShowSourceGroundedProposal(false);
            setSelectedContract(null);
          }}
          onSuccess={(_proposal: GeneratedProposal, _collectedData: CollectedData, _contractData: ContractData) => {
            // Single viewer: open the freshly generated proposal in the Workspace.
            setShowSourceGroundedProposal(false);
            loadProposalStatus();
            handleViewProposal(selectedContract);
          }}
        />
      )}

      {/* Basic Proposal Generator */}
      {showProposalGenerator && selectedContract && (
        <ProposalGenerator
          contract={selectedContract}
          onClose={() => {
            setShowProposalGenerator(false);
            setSelectedContract(null);
          }}
          onSuccess={() => {
            setShowProposalGenerator(false);
            loadProposalStatus();
            handleViewProposal(selectedContract);
          }}
          existingProposal={false}
        />
      )}

      {/* Proposal Draft Modal */}
      {showProposalDraft && selectedContract && proposalDraft && (
        <ProposalDraftModal
          draft={proposalDraft}
          contract={selectedContract}
          onClose={() => {
            setShowProposalDraft(false);
            setSourceGroundedOutput(null);
            setSelectedContract(null);
          }}
          onUpdate={async (updatedDraft: any) => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const noticeId = selectedContract.notice_id || selectedContract.id;
            await supabase
              .from('proposal_templates')
              .update({ content_json: updatedDraft })
              .eq('notice_id', noticeId)
              .eq('profile_id', user.id);

            setProposalDraft(updatedDraft);
            toast.success('Proposal updated');
          }}
          onDelete={() => {}}
          onExport={() => {}}
          sourceGroundedOutput={sourceGroundedOutput || undefined}
        />
      )}

      {/* Admin Panel */}
      {showAdminPanel && (
        <AdminDataManager
          onClose={() => {
            setShowAdminPanel(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

export default ModernDashboard;
